import React, { useState, useEffect } from 'react';
import VisibilityBadges from './VisibilityBadges';
import TableDetailPanel from './TableDetailPanel';
import LocationFilter from './LocationFilter';

const PAGE_SIZE = 25;

const TABS = [
  { key: 'menus', label: '📋 Menus', endpoint: 'menus' },
  { key: 'menu-groups', label: '📁 Menu Groups', endpoint: 'menu-groups' },
  { key: 'menu-items', label: '🍽️ Menu Items', endpoint: 'menu-items' },
  { key: 'products', label: '📦 Products', endpoint: 'products' },
  { key: 'modifier-groups', label: '🔧 Modifier Groups', endpoint: 'modifier-groups' },
  { key: 'modifiers', label: '➕ Modifiers', endpoint: 'modifiers' }
];

export default function TableViews({ locationId, locationName }) {
  const [activeTab, setActiveTab] = useState('menus');
  const [locationFilter, setLocationFilter] = useState(locationId || '');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    setLocationFilter(locationId || '');
  }, [locationId]);

  useEffect(() => {
    fetchTableData(activeTab);
  }, [activeTab, locationFilter]);

  useEffect(() => {
    setPage(1);
    setViewing(null);
  }, [activeTab, locationFilter]);

  async function fetchTableData(tab) {
    try {
      setLoading(true);
      setError(null);
      const tabConfig = TABS.find(t => t.key === tab);
      if (!tabConfig) return;

      const query = locationFilter ? `?locationId=${locationFilter}` : '';
      const response = await fetch(`/api/tables/${tabConfig.endpoint}${query}`);
      if (!response.ok) throw new Error('Failed to fetch data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const totalPages = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const pageData = data.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showLocationColumn = !locationFilter;

  const tableProps = { data: pageData, onView: setViewing, showLocationColumn };

  return (
    <div className="table-views-container">
      <h2>📊 Data Tables {locationFilter && locationName ? `- ${locationName}` : ''}</h2>

      <LocationFilter value={locationFilter} onChange={setLocationFilter} />

      <div className="table-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`table-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="table-content">
        {loading && <p className="loading">Loading...</p>}
        {error && <div className="error">✕ {error}</div>}

        {!loading && !error && (
          <>
            {activeTab === 'menus' && <MenusTable {...tableProps} />}
            {activeTab === 'menu-groups' && <MenuGroupsTable {...tableProps} />}
            {activeTab === 'menu-items' && <MenuItemsTable {...tableProps} />}
            {activeTab === 'products' && <ProductsTable {...tableProps} />}
            {activeTab === 'modifier-groups' && <ModifierGroupsTable {...tableProps} />}
            {activeTab === 'modifiers' && <ModifiersTable {...tableProps} />}

            {data.length > 0 && (
              <div className="pagination">
                <button onClick={() => setPage(1)} disabled={page === 1}>First</button>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
                <span className="pagination-info">Page {page} of {totalPages} · {data.length} rows</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
                <button onClick={() => setPage(totalPages)} disabled={page === totalPages}>Last</button>
              </div>
            )}

            {viewing && (
              <TableDetailPanel item={viewing} onClose={() => setViewing(null)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ViewButton({ onClick }) {
  return (
    <button className="view-icon-btn" onClick={onClick} title="View details">
      👁️
    </button>
  );
}

function MenusTable({ data, onView, showLocationColumn }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Menu Name</th>
            {showLocationColumn && <th>Location</th>}
            <th>Description</th>
            <th>Groups</th>
            <th>Items</th>
            <th>Synced</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map(menu => (
            <tr key={menu.guid}>
              <td className="name-cell">{menu.name}</td>
              {showLocationColumn && (
                <td><span className="location-cell" title={menu.location_guid}>{menu.location_name || '-'}</span></td>
              )}
              <td>{menu.description || '-'}</td>
              <td className="count-badge">{menu.group_count}</td>
              <td className="count-badge">{menu.item_count}</td>
              <td className="date-cell">{new Date(menu.synced_at).toLocaleDateString()}</td>
              <td><ViewButton onClick={() => onView({ type: 'menu', row: menu })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="empty-message">No menus found</p>}
    </div>
  );
}

function MenuGroupsTable({ data, onView, showLocationColumn }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Group Name</th>
            {showLocationColumn && <th>Location</th>}
            <th>Menu</th>
            <th>Parent Group</th>
            <th>Items</th>
            <th>Synced</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map(group => (
            <tr key={group.guid}>
              <td className="name-cell">{group.name}</td>
              {showLocationColumn && (
                <td><span className="location-cell" title={group.location_guid}>{group.location_name || '-'}</span></td>
              )}
              <td>{group.menu_name || '-'}</td>
              <td>{group.parent_group_name ? `└─ ${group.parent_group_name}` : 'Root'}</td>
              <td className="count-badge">{group.item_count}</td>
              <td className="date-cell">{new Date(group.synced_at).toLocaleDateString()}</td>
              <td><ViewButton onClick={() => onView({ type: 'menu-group', row: group })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="empty-message">No menu groups found</p>}
    </div>
  );
}

function MenuItemsTable({ data, onView, showLocationColumn }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Item Name</th>
            {showLocationColumn && <th>Location</th>}
            <th>Menu</th>
            <th>Group</th>
            <th>Price</th>
            <th>Visibility</th>
            <th>Modifiers</th>
            <th>Images</th>
            <th>Synced</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map(item => (
            <tr key={item.guid}>
              <td className="name-cell">{item.name}</td>
              {showLocationColumn && (
                <td><span className="location-cell" title={item.location_guid}>{item.location_name || '-'}</span></td>
              )}
              <td>{item.menu_name || '-'}</td>
              <td>{item.menu_group_name || '-'}</td>
              <td className="price-cell">${(item.price || 0).toFixed(2)}</td>
              <td><VisibilityBadges visibility={item.visibility} /></td>
              <td className="count-badge">{item.modifier_count}</td>
              <td className="count-badge">{item.image_count}</td>
              <td className="date-cell">{new Date(item.synced_at).toLocaleDateString()}</td>
              <td><ViewButton onClick={() => onView({ type: 'product', row: item })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="empty-message">No menu items found</p>}
    </div>
  );
}

function ProductsTable({ data, onView, showLocationColumn }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Product Name</th>
            {showLocationColumn && <th>Location</th>}
            <th>Menu</th>
            <th>Group</th>
            <th>Price</th>
            <th>Category</th>
            <th>Visibility</th>
            <th>Taxes</th>
            <th>Images</th>
            <th>Created</th>
            <th>Updated</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map(product => (
            <tr key={product.guid}>
              <td className="name-cell">{product.name}</td>
              {showLocationColumn && (
                <td><span className="location-cell" title={product.location_guid}>{product.location_name || '-'}</span></td>
              )}
              <td>{product.menu_name || '-'}</td>
              <td>{product.menu_group_name || '-'}</td>
              <td className="price-cell">${(product.price || 0).toFixed(2)}</td>
              <td>{product.sales_category_name || '-'}</td>
              <td><VisibilityBadges visibility={product.visibility} /></td>
              <td className="count-badge">{product.tax_count}</td>
              <td className="count-badge">{product.image_count}</td>
              <td className="date-cell">{product.created_at ? new Date(product.created_at).toLocaleDateString() : '-'}</td>
              <td className="date-cell">{product.synced_at ? new Date(product.synced_at).toLocaleDateString() : '-'}</td>
              <td><ViewButton onClick={() => onView({ type: 'product', row: product })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="empty-message">No products found</p>}
    </div>
  );
}

function ModifierGroupsTable({ data, onView, showLocationColumn }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Group Name</th>
            {showLocationColumn && <th>Location</th>}
            <th>Select Range</th>
            <th>Modifiers</th>
            <th>Used By Items</th>
            <th>Synced</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map(group => (
            <tr key={group.guid}>
              <td className="name-cell">{group.name}</td>
              {showLocationColumn && (
                <td><span className="location-cell" title={group.location_guid}>{group.location_name || '-'}</span></td>
              )}
              <td>{group.min_selected} - {group.max_selected}</td>
              <td className="count-badge">{group.modifier_count}</td>
              <td className="count-badge">{group.item_count}</td>
              <td className="date-cell">{new Date(group.synced_at).toLocaleDateString()}</td>
              <td><ViewButton onClick={() => onView({ type: 'modifier-group', row: group })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="empty-message">No modifier groups found</p>}
    </div>
  );
}

function ModifiersTable({ data, onView, showLocationColumn }) {
  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Modifier Name</th>
            {showLocationColumn && <th>Location</th>}
            <th>Modifier Group</th>
            <th>Price Adjustment</th>
            <th>Sort Order</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.map(modifier => (
            <tr key={modifier.guid}>
              <td className="name-cell">{modifier.name}</td>
              {showLocationColumn && (
                <td><span className="location-cell" title={modifier.location_guid}>{modifier.location_name || '-'}</span></td>
              )}
              <td>{modifier.modifier_group_name || '-'}</td>
              <td className="price-cell">
                {modifier.price_adjustment ? `+$${modifier.price_adjustment.toFixed(2)}` : 'No charge'}
              </td>
              <td>{modifier.sort_order ?? '-'}</td>
              <td><ViewButton onClick={() => onView({ type: 'modifier', row: modifier })} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && <p className="empty-message">No modifiers found</p>}
    </div>
  );
}
